import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiHelper } from "../../../utils/apiHelper";
import { toast } from "react-toastify";

export const createPatentDocument = createAsyncThunk(
    "documents/createPatentDocument",
    async (documentData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.post("/startup/patent-document", documentData);
            if (response.data.code === "00000") {
                toast.success("Patent belgesi başarıyla oluşturuldu");
                return documentData;
            } else {
                throw new Error("Patent document creation failed");
            }
        } catch (error) {
            toast.error("Patent belgesi oluşturulurken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const fetchPatentDocuments = createAsyncThunk(
    "documents/fetchPatentDocuments",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/patent-documents`);
            if (response.data.code === "00000") {
                return response.data.startupPatentDocuments;
            } else {
                throw new Error("Failed to fetch patent documents");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const deletePatentDocument = createAsyncThunk(
    "documents/deletePatentDocument",
    async (patentDocumentId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.delete('/startup/patent-document', {
                data: { patentDocumentId }
            });
            if (response.data.code === "00000") {
                toast.success("Patent belgesi başarıyla silindi");
                return patentDocumentId;
            } else {
                throw new Error("Patent document deletion failed");
            }
        } catch (error) {
            toast.error("Patent belgesi silinirken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const createAwardDocument = createAsyncThunk(
    "documents/createAwardDocument",
    async (documentData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.post("/startup/award-document", documentData);
            if (response.data.code === "00000") {
                toast.success("Ödül belgesi başarıyla oluşturuldu");
                return response.data.awardDocument;
            } else {
                throw new Error("Award document creation failed");
            }
        } catch (error) {
            toast.error("Ödül belgesi oluşturulurken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const fetchAwardDocuments = createAsyncThunk(
    "documents/fetchAwardDocuments",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/award-documents`);
            if (response.data.code === "00000") {
                return response.data.startupAwardAchievements;
            } else {
                throw new Error("Failed to fetch award documents");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const deleteAwardDocument = createAsyncThunk(
    "documents/deleteAwardDocument",
    async (awardDocumentId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.delete('/startup/award-document', {
                data: { awardDocumentId }
            });
            if (response.data.code === "00000") {
                toast.success("Ödül belgesi başarıyla silindi");
                return awardDocumentId;
            } else {
                throw new Error("Award document deletion failed");
            }
        } catch (error) {
            toast.error("Ödül belgesi silinirken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const createPermissionDocument = createAsyncThunk(
    "documents/createPermissionDocument",
    async (documentData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.post("/startup/permission-document", documentData);
            if (response.data.code === "00000") {
                toast.success("İzin belgesi başarıyla oluşturuldu");
                return documentData;
            } else {
                throw new Error("Permission document creation failed");
            }
        } catch (error) {
            toast.error("İzin belgesi oluşturulurken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const fetchPermissionDocuments = createAsyncThunk(
    "documents/fetchPermissionDocuments",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/permission-documents`);
            if (response.data.code === "00000") {
                return response.data.startupPermissionApprovals;
            } else {
                throw new Error("Failed to fetch permission documents");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const deletePermissionDocument = createAsyncThunk(
    "documents/deletePermissionDocument",
    async (permissionsApprovalId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.delete('/startup/permission-document', {
                data: { permissionsApprovalId }
            });
            if (response.data.code === "00000") {
                toast.success("İzin belgesi başarıyla silindi");
                return permissionsApprovalId;
            } else {
                throw new Error("Permission document deletion failed");
            }
        } catch (error) {
            toast.error("İzin belgesi silinirken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

const documentSlice = createSlice({
    name: "documents",
    initialState: {
        patentDocuments: [],
        awardDocuments: [],
        permissionDocuments: [],
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(createPatentDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createPatentDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.patentDocuments.push(action.payload);
            })
            .addCase(createPatentDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchPatentDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPatentDocuments.fulfilled, (state, action) => {
                state.loading = false;
                state.patentDocuments = action.payload;
            })
            .addCase(fetchPatentDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(deletePatentDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deletePatentDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.patentDocuments = state.patentDocuments.filter(
                    doc => doc.patentDocumentId !== action.payload
                );
            })
            .addCase(deletePatentDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createAwardDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createAwardDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.awardDocuments.push(action.payload);
            })
            .addCase(createAwardDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchAwardDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAwardDocuments.fulfilled, (state, action) => {
                state.loading = false;
                state.awardDocuments = action.payload;
            })
            .addCase(fetchAwardDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(deleteAwardDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteAwardDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.awardDocuments = state.awardDocuments.filter(
                    doc => doc.awardDocumentId !== action.payload
                );
            })
            .addCase(deleteAwardDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createPermissionDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createPermissionDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.permissionDocuments.push(action.payload);
            })
            .addCase(createPermissionDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchPermissionDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPermissionDocuments.fulfilled, (state, action) => {
                state.loading = false;
                state.permissionDocuments = action.payload;
            })
            .addCase(fetchPermissionDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(deletePermissionDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deletePermissionDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.permissionDocuments = state.permissionDocuments.filter(
                    doc => doc.permissionsApprovalId !== action.payload
                );
            })
            .addCase(deletePermissionDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export default documentSlice.reducer;

